import { openProject } from '../services/projects.js';

/* WHAT EVERY EXPORT IS CALLED, decided in one place.
 *
 * Six call sites used to build their own string, and they had drifted: the
 * deliverable said `illusio3d_1080x1920`, the mockup `illusio3d_onsite_...`,
 * the panels inside a ZIP `illusio3d_panelr1c2_...` with the key run straight
 * onto the word. None of them said which PROJECT they came from, so a folder
 * holding a week's exports from three jobs was a list of near-identical names.
 *
 *   Illusio3D_{Kind}_{Project}_{spec}[_{fps}fps].{ext}
 *
 * Kind and Project are both optional — the plain deliverable has no kind, and
 * an unsaved scene has no name worth printing — and everything is Headline
 * Case, so the files read as titles rather than as slugs.
 */

/* Left lowercase inside a headline, capitalised at either end. The list is the
   short conventional one; anything longer starts making decisions about the
   user's own words that a filename has no business making. */
const MINOR = new Set(['a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'from',
                       'in', 'nor', 'of', 'on', 'or', 'the', 'to', 'vs', 'with']);

const cap = w => w.charAt(0).toUpperCase() + w.slice(1);

function headline(words) {
  const last = words.length - 1;
  return words.map((w, i) => {
    /* Left exactly as typed when it already carries capitals of its own or
       starts with a digit: 3D, LCD, iPhone, 4K, 2024 all mean something as
       written, and title-casing them would be a correction nobody asked for. */
    if (/[A-Z]/.test(w.slice(1)) || /^\d/.test(w)) return w;
    const lower = w.toLowerCase();
    return i > 0 && i < last && MINOR.has(lower) ? lower : cap(lower);
  });
}

/**
 * The open project's title as one filename segment, or '' when there is not a
 * real one to use. Every run of non-alphanumerics becomes a single underscore,
 * which handles the illegal characters and the spaces in the same pass.
 *
 * @param {string} [title]
 */
export function projectSlug(title = openProject.title) {
  const words = String(title || '').split(/[^A-Za-z0-9]+/).filter(Boolean);
  /* 'Untitled' is what a scene is called before anyone has named it — printing
     it puts a word in the filename that carries no information at all. */
  if (!words.length || (words.length === 1 && words[0].toLowerCase() === 'untitled')) return '';
  let out = headline(words).join('_');
  // Trimmed at a word boundary rather than mid-word, well clear of path limits.
  if (out.length > 60) out = out.slice(0, 60).replace(/_[^_]*$/, '');
  return out;
}

/**
 * @param {object} p
 * @param {string} [p.kind]  Onsite, Wall_3x2, Corner, Panel_R1C2 — omitted for
 *                           the plain deliverable, which needs no qualifier
 * @param {string} [p.spec]  the dimensions, e.g. '1920x1080'
 * @param {number} [p.fps]   video only
 * @param {string} p.ext
 */
export function exportName({ kind = '', spec = '', fps = 0, ext }) {
  const parts = ['Illusio3D'];
  if (kind) parts.push(kind);
  const proj = projectSlug();
  if (proj) parts.push(proj);
  if (spec) parts.push(spec);
  if (fps) parts.push(fps + 'fps');
  return parts.join('_') + '.' + ext;
}
